import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, X } from "lucide-react";
import type { Contact } from "@lumika/shared";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  cn,
} from "@lumika/ui";
import { bdcApi } from "../api/bdc";
import { contactDisplayName } from "../lib/contact";

/**
 * Debounced, server-side contact picker. Replaces the fetch-all-contacts
 * Select on the appointment/task dialogs: it queries
 * `contacts({ search, pageSize })` as the operator types (250 ms debounce)
 * instead of loading the whole contact table up front. A preselected
 * `value` (a contactId) is hydrated via `contact(id)` so its label shows
 * even before any search runs. `onSelect` receives the full Contact so the
 * caller can derive the rooftop.
 */
export function ContactSearchPicker({
  value,
  onSelect,
  disabled,
  placeholder = "Search contacts…",
}: {
  value: string;
  onSelect: (contact: Contact | null) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  // The label of the currently-selected contact, kept so the trigger keeps
  // showing the name when the dropdown is closed.
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  // Debounce the typed query (matches ContactsPage's 250 ms convention).
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Hydrate the preselected contact's label so a value set from outside
  // (e.g. editing) renders a name without loading the whole list.
  const selectedQuery = useQuery({
    queryKey: ["bdc", "contact", value],
    queryFn: () => bdcApi.contact(value),
    enabled: !!value && !selectedLabel,
  });
  useEffect(() => {
    if (selectedQuery.data) setSelectedLabel(contactDisplayName(selectedQuery.data));
  }, [selectedQuery.data]);
  useEffect(() => {
    if (!value) setSelectedLabel(null);
  }, [value]);

  const resultsQuery = useQuery({
    queryKey: ["bdc", "contacts", "search", debounced],
    queryFn: () => bdcApi.contacts({ search: debounced, pageSize: 20 }),
    enabled: open,
  });
  const results = useMemo(
    () => resultsQuery.data?.items ?? [],
    [resultsQuery.data],
  );

  // Arrow-key highlight over the results; reset whenever the result
  // set changes or the dropdown re-opens.
  const [activeIndex, setActiveIndex] = useState(-1);
  useEffect(() => {
    setActiveIndex(-1);
  }, [debounced, open]);

  function choose(contact: Contact) {
    setSelectedLabel(contactDisplayName(contact));
    onSelect(contact);
    setOpen(false);
    setSearch("");
  }

  function clear() {
    setSelectedLabel(null);
    onSelect(null);
    setSearch("");
  }

  function setDialogOpen(nextOpen: boolean) {
    setOpen(nextOpen);
    if (!nextOpen) setSearch("");
  }

  return (
    <div className="relative">
      <Dialog open={open} onOpenChange={setDialogOpen}>
        <DialogTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="flex h-touch w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className={cn("line-clamp-1 text-left", !selectedLabel && "text-muted-foreground")}>
              {selectedLabel ?? placeholder}
            </span>
            <span className="flex items-center gap-1">
              {/* Reserves the slot the absolute clear button overlays. */}
              {selectedLabel ? <span className="h-4 w-4" aria-hidden /> : null}
              <ChevronDown className="h-4 w-4 opacity-50" />
            </span>
          </button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select a contact</DialogTitle>
            <DialogDescription>
              Search by name, email, or phone number.
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            role="combobox"
            aria-controls="contact-search-results"
            aria-expanded="true"
            aria-activedescendant={
              results[activeIndex]?.id
                ? `contact-search-option-${results[activeIndex]!.id}`
                : undefined
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActiveIndex((i) => Math.min(i + 1, results.length - 1));
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setActiveIndex((i) => Math.max(i - 1, 0));
              }
              if (e.key === "Enter") {
                e.preventDefault();
                const active = results[activeIndex];
                if (active) choose(active);
              }
            }}
            placeholder={placeholder}
          />
          <DialogBody className="max-h-72 px-0 sm:px-0">
            <div
              id="contact-search-results"
              role="listbox"
              aria-label="Contact results"
              className="p-1"
            >
              {resultsQuery.isLoading ? (
                <p className="px-2 py-3 text-sm text-muted-foreground">
                  Searching…
                </p>
              ) : results.length === 0 ? (
                <p className="px-2 py-3 text-sm text-muted-foreground">
                  {debounced ? "No contacts match" : "Type to search contacts"}
                </p>
              ) : (
                results.map((contact, index) => (
                  <button
                    key={contact.id}
                    id={`contact-search-option-${contact.id}`}
                    type="button"
                    role="option"
                    aria-selected={contact.id === value}
                    onClick={() => choose(contact)}
                    className={cn(
                      "flex min-h-10 w-full items-center rounded-md px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground",
                      contact.id === value && "bg-accent/50",
                      index === activeIndex &&
                        "bg-accent text-accent-foreground",
                    )}
                  >
                    <span className="line-clamp-1">
                      {contactDisplayName(contact)}
                    </span>
                  </button>
                ))
              )}
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
      {/* A real sibling button (buttons cannot nest) keeps Clear separately
          focusable while the entire field remains the picker trigger. */}
      {selectedLabel ? (
        <button
          type="button"
          aria-label="Clear selected contact"
          disabled={disabled}
          onClick={clear}
          className="absolute right-8 top-1/2 -translate-y-1/2 rounded-sm opacity-50 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring disabled:pointer-events-none"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}
